import type { OrgPathFields } from '@/lib/orgPaths';
import {
  buildOrgAbsoluteUrl,
  buildOrgLoginPath,
  buildOrgPath,
  getOrgHostMode,
} from '@/lib/orgPaths';
import { useOrgSlug } from './useOrgSlug';
import { useOrgMemberStore } from '@/store/orgMember';

function toPathFields(
  org: OrgPathFields | null | undefined,
  slug: string | undefined
): OrgPathFields | null {
  if (org) return org;
  if (slug) return { slug };
  return null;
}

/**
 * Path helpers for the unified member portal under /club-academy/:slug.
 */
export function useOrgPaths(org?: OrgPathFields | null) {
  const slug = useOrgSlug();
  const storeOrg = useOrgMemberStore((s) => s.orgProfile?.organization);
  const hostMode = getOrgHostMode();
  const fields = toPathFields(
    org ?? (storeOrg ? { slug: storeOrg.slug, subdomain: storeOrg.subdomain, subdomainEnabled: storeOrg.subdomainEnabled } : null),
    slug
  );

  if (!fields) {
    return {
      slug,
      hostMode,
      fields: null as OrgPathFields | null,
      base: null as string | null,
      p: (_subpath: string) => null as string | null,
      login: (_returnPath?: string) => null as string | null,
      absolute: (_subpath?: string) => null as string | null,
    };
  }

  const base = buildOrgPath(fields, '', hostMode) || '/';
  const p = (subpath: string) => buildOrgPath(fields, subpath, hostMode);
  const login = (returnPath?: string) => buildOrgLoginPath(fields, returnPath, hostMode);
  const absolute = (subpath = '') => buildOrgAbsoluteUrl(fields, subpath, undefined, hostMode);

  return {
    slug: slug ?? fields.slug,
    hostMode,
    fields,
    base,
    p,
    login,
    absolute,
  };
}
